import { TextBody } from '@/design-system/components/typography';
import { keyGenerator } from '@/utils/keyGenerator';
import { ScrollView, styled, SxProp, useDripsyTheme, View } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable } from 'react-native';
import { CountrysData } from '@/features/card-account/components/countrySelector/useCountrySelector';

const PressableView = styled(Pressable)();

interface CountrySelectorDropdownMenuProps {
  hasFocus: boolean;
  displayCountryData: CountrysData;
  changeFocus: (value: boolean) => void;
  selectCountryId: (id: string) => void;
  onValueChange: (value: string) => void;
  isLoading: boolean;
}

export const CountrySelectorDropdownMenu: React.FC<CountrySelectorDropdownMenuProps> = ({
  hasFocus,
  displayCountryData,
  changeFocus,
  selectCountryId,
  onValueChange,
  isLoading
}) => {
  if (!hasFocus) return null;

  const { t } = useTranslation('earn');
  const { theme } = useDripsyTheme();

  return (
    <View sx={styles.dropdownMenu}>
      {!isLoading ? (
        <ScrollView>
          {displayCountryData.length === 0 ? (
            <View sx={{ alignItems: 'center', paddingX: '$3' }}>
              <TextBody>{t('earn-invest::glossary.search-no-result')}</TextBody>
            </View>
          ) : null}
          {displayCountryData.length > 0
            ? displayCountryData.map((item) => (
                <PressableView
                  key={keyGenerator('account-selector')}
                  onPress={() => {
                    selectCountryId(item?.code || '');
                    changeFocus(false);
                    onValueChange(item?.code || '');
                  }}>
                  {({ hovered }) => (
                    <View sx={{ ...styles.dropdownItem, backgroundColor: hovered ? '$backgroundDropdownActive' : '$backgroundPopup' }}>
                      <View sx={{ flex: 1 }}>
                        <TextBody label>{item.name}</TextBody>
                      </View>
                    </View>
                  )}
                </PressableView>
              ))
            : null}
        </ScrollView>
      ) : (
        <View sx={{ padding: '$5' }}>
          <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
        </View>
      )}
    </View>
  );
};

enum StyleName {
  dropdownMenu = 'dropdownMenu',
  dropdownItem = 'dropdownItem'
}

const styles: Record<StyleName, SxProp> = {
  dropdownMenu: {
    position: 'absolute',
    top: '$15',
    left: 0,
    width: '100%',
    maxHeight: 250,
    paddingY: '$2',
    backgroundColor: '$backgroundPopup',
    borderRadius: 5,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10
  },
  dropdownItem: {
    paddingX: '$4',
    paddingY: '$3',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$3'
  }
};
